import React from "react";
import {
  Award,
  ArrowUpRight,
  CalendarPlus,
  Briefcase,
  Building,
  MapPin,
  Star,
  UserCheck,
  Users,
  HeartHandshake,
  GraduationCap,
  PlaneTakeoff,
  PlaneLanding,
  TrendingUp,
  Sparkles,
  Crown,
  ShieldAlert,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";

const EVENT_META = {
  joined: { icon: CalendarPlus, color: "bg-blue-50 text-blue-700 border-blue-200", label: "Joined" },
  confirmed: { icon: UserCheck, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Confirmed" },
  promoted: { icon: TrendingUp, color: "bg-amber-50 text-amber-700 border-amber-200", label: "Promotion" },
  role_changed: { icon: Briefcase, color: "bg-slate-50 text-slate-700 border-slate-200", label: "Role change" },
  department_changed: { icon: Building, color: "bg-slate-50 text-slate-700 border-slate-200", label: "Department change" },
  business_unit_changed: { icon: Building, color: "bg-slate-50 text-slate-700 border-slate-200", label: "Business unit change" },
  location_changed: { icon: MapPin, color: "bg-slate-50 text-slate-700 border-slate-200", label: "Location change" },
  manager_changed: { icon: Users, color: "bg-violet-50 text-violet-700 border-violet-200", label: "Manager change" },
  employment_type_changed: { icon: Briefcase, color: "bg-slate-50 text-slate-700 border-slate-200", label: "Employment type" },
  leave_started: { icon: PlaneTakeoff, color: "bg-amber-50 text-amber-700 border-amber-200", label: "Leave started" },
  leave_ended: { icon: PlaneLanding, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Leave ended" },
  cycle_rating_received: { icon: Star, color: "bg-amber-50 text-amber-700 border-amber-200", label: "Cycle rating" },
  major_objective_completed: { icon: Award, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Objective completed" },
  kudos_milestone: { icon: HeartHandshake, color: "bg-rose-50 text-rose-700 border-rose-200", label: "Kudos milestone" },
  feedback_milestone: { icon: Sparkles, color: "bg-violet-50 text-violet-700 border-violet-200", label: "Feedback milestone" },
  development_plan_started: { icon: GraduationCap, color: "bg-blue-50 text-blue-700 border-blue-200", label: "Development plan" },
  development_plan_completed: { icon: GraduationCap, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Plan completed" },
  hipo_flagged: { icon: Crown, color: "bg-amber-50 text-amber-700 border-amber-200", label: "HiPo flagged" },
  exited: { icon: LogOut, color: "bg-slate-100 text-slate-700 border-slate-200", label: "Exited" },
  rehired: { icon: ArrowUpRight, color: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Rehired" },
};

function format(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

export default function CareerTimeline({ events }) {
  if (!events?.length) {
    return (
      <div className="text-sm text-slate-500" data-testid="timeline-empty">
        No timeline events yet.
      </div>
    );
  }
  return (
    <div className="relative pl-6" data-testid="career-timeline">
      <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
      <div className="space-y-4">
        {events.map((e, i) => {
          const meta = EVENT_META[e.event_type] || {
            icon: ShieldAlert,
            color: "bg-slate-50 text-slate-700 border-slate-200",
            label: e.event_type,
          };
          const Icon = meta.icon;
          return (
            <div key={e.id || i} className="relative" data-testid={`timeline-event-${e.event_type}`}>
              <div className={cn("absolute -left-[18px] top-2 w-[14px] h-[14px] rounded-full border-2 border-white", meta.color.split(" ").find(c => c.startsWith("bg-")) || "bg-slate-300")} />
              <div className="axia-card p-4 hover:border-slate-300 transition">
                <div className="flex items-start gap-3">
                  <div className={cn("w-8 h-8 rounded-md flex items-center justify-center border", meta.color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="font-display text-sm font-semibold text-slate-900">{meta.label}</div>
                      <div className="font-mono text-[11px] text-slate-500">{format(e.event_date)}</div>
                    </div>
                    {e.details && Object.keys(e.details).length > 0 && (
                      <dl className="mt-2 text-xs text-slate-600 space-y-0.5">
                        {Object.entries(e.details).map(([k, v]) => (
                          <div key={k} className="flex gap-2">
                            <dt className="text-slate-400 capitalize">{k.replace(/_/g, " ")}:</dt>
                            <dd className="font-medium">{String(v ?? "")}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    <div className="mt-2 axia-overline text-slate-400">{e.source}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
